import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/utils.helpers";
import { Card } from "@/components/ui/card";
import { Users, UserCheck, UserX, Clock, Wallet, CalendarRange, TrendingDown, Coins } from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <Card className="border-[#E5E2DC] shadow-sm p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold">{label}</div>
        <div className="text-2xl md:text-3xl font-serif font-semibold mt-2 text-slate-900 truncate">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setD(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!d) return <div className="text-slate-500">Loading dashboard…</div>;
  const attTotal = d.today.present + d.today.absent + d.today.half_day + d.today.leave + d.today.weekly_off + d.today.holiday;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Overview</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Dashboard</h1>
        <p className="text-slate-600 mt-1">Payroll cycle: <span className="font-medium text-slate-900">{d.cycle.label}</span></p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Stat testid="stat-total-employees"
              label="Total Employees"
              value={d.total_employees}
              sub={`${d.active_employees} active`}
              icon={Users} accent="bg-[#111111]" />
        <Stat testid="stat-today-attendance"
              label="Today's Attendance"
              value={`${d.today.present}/${d.active_employees}`}
              sub={`${d.today.absent} absent · ${d.today.half_day} half · ${d.today.leave} leave`}
              icon={UserCheck} accent="bg-green-600" />
        <Stat testid="stat-monthly-payroll"
              label="Monthly Payroll (Gross)"
              value={formatINR(d.cycle_totals.gross)}
              sub="Current cycle 24–23"
              icon={Wallet} accent="bg-[#C59B27]" />
        <Stat testid="stat-advance-balance"
              label="Advance Balance"
              value={formatINR(d.cycle_totals.advance_balance)}
              sub="Total advances this cycle"
              icon={TrendingDown} accent="bg-orange-500" />
        <Stat testid="stat-pending-salary"
              label="Pending Salary"
              value={formatINR(d.cycle_totals.remaining_balance)}
              sub="Yet to be paid"
              icon={Coins} accent="bg-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-[#E5E2DC] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-[#111111] flex items-center justify-center"><CalendarRange className="w-4 h-4 text-white" /></div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Today · {formatDate(d.today.date)}</div>
              <div className="font-serif text-2xl text-slate-900 mt-1">{attTotal} of {d.active_employees} marked</div>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
            <Cell label="Present" value={d.today.present} color="text-green-700 bg-green-50" />
            <Cell label="Half Day" value={d.today.half_day} color="text-yellow-700 bg-yellow-50" />
            <Cell label="Absent" value={d.today.absent} color="text-red-700 bg-red-50" />
            <Cell label="Leave" value={d.today.leave} color="text-blue-700 bg-blue-50" />
            <Cell label="Wk Off" value={d.today.weekly_off} color="text-slate-700 bg-slate-100" />
            <Cell label="Holiday" value={d.today.holiday} color="text-purple-700 bg-purple-50" />
          </div>
        </Card>

        <Card className="border-[#E5E2DC] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-md bg-[#C59B27] flex items-center justify-center"><Clock className="w-4 h-4 text-white" /></div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Net Payable This Cycle</div>
              <div className="font-serif text-3xl text-slate-900 mt-1" data-testid="stat-net-payable">{formatINR(d.cycle_totals.net_payable)}</div>
            </div>
          </div>
          <div className="text-sm text-slate-600">
            Gross earnings after subtracting advances and deductions for all active employees in the current 24–23 cycle.
          </div>
        </Card>
      </div>
    </div>
  );
}

const Cell = ({ label, value, color }) => (
  <div className={`rounded-md p-3 text-center ${color}`}>
    <div className="text-[10px] uppercase tracking-wider font-bold">{label}</div>
    <div className="font-serif text-xl mt-1">{value}</div>
  </div>
);
